import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { GEO } from "@/constants/testIds";

export default function GeographicBrowser() {
  const [states, setStates] = useState([]);
  const [country, setCountry] = useState("India");
  const [state, setState] = useState("Kerala");
  const [district, setDistrict] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/heritage/states").then((r) => {
      setStates(r.data.states || []);
    }).catch(() => {});
  }, []);

  const current = useMemo(
    () => states.find((s) => s.name === state) || null,
    [states, state]
  );

  function onSubmit(e) {
    e.preventDefault();
    setResult({
      country,
      state,
      district: district.trim() || "Any district",
      trees: current ? current.trees : 0,
      motif: current ? current.motif : "",
    });
  }

  return (
    <section
      id="heritage"
      data-testid={GEO.root}
      className="relative py-28 md:py-40 bg-tulsi text-ivory overflow-hidden"
    >
      {/* soft banyan */}
      <img
        aria-hidden
        src="https://images.unsplash.com/photo-1783768272434-cd6d2ea34a2a?crop=entropy&cs=srgb&fm=jpg&w=1200&q=60"
        className="absolute -left-40 -bottom-32 w-[900px] opacity-[0.08] mix-blend-screen pointer-events-none"
        alt=""
      />
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-8">
        <div className="col-span-12 lg:col-span-5">
          <span className="text-xs uppercase tracking-[0.3em] text-marigold">Discover your ancestral village</span>
          <h2 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[0.95]">
            Every family
            <span className="italic text-marigold"> comes </span>
            from somewhere.
          </h2>
          <p className="mt-6 text-ivory/70 text-lg font-light max-w-md leading-relaxed">
            Browse Country → State → District → Village. Historical boundaries, old maps,
            population history and migration routes for the place your grandparents called home.
          </p>

          <form onSubmit={onSubmit} className="mt-10 space-y-5">
            <div>
              <label className="text-[10px] uppercase tracking-[0.28em] text-ivory/60">Country</label>
              <select
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                data-testid={GEO.countrySelect}
                className="mt-2 w-full h-12 bg-transparent border border-ivory/25 focus:border-marigold focus:outline-none rounded-none px-4 text-ivory font-serif text-lg"
              >
                <option className="text-ink">India</option>
                <option className="text-ink">United States</option>
                <option className="text-ink">United Kingdom</option>
                <option className="text-ink">Canada</option>
                <option className="text-ink">Australia</option>
                <option className="text-ink">United Arab Emirates</option>
                <option className="text-ink">Kenya</option>
                <option className="text-ink">Singapore</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.28em] text-ivory/60">State</label>
              <select
                value={state}
                onChange={(e) => setState(e.target.value)}
                data-testid={GEO.stateSelect}
                className="mt-2 w-full h-12 bg-transparent border border-ivory/25 focus:border-marigold focus:outline-none rounded-none px-4 text-ivory font-serif text-lg"
              >
                {states.map((s) => <option key={s.name} className="text-ink">{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.28em] text-ivory/60">District or village (optional)</label>
              <input
                value={district}
                onChange={(e) => setDistrict(e.target.value)}
                data-testid={GEO.districtInput}
                placeholder="e.g., Palakkad, Kalpathy…"
                className="mt-2 w-full h-12 bg-transparent border border-ivory/25 focus:border-marigold focus:outline-none rounded-none px-4 text-ivory font-serif text-lg placeholder:text-ivory/30"
              />
            </div>
            <button
              type="submit"
              data-testid={GEO.submit}
              className="mt-2 inline-flex items-center gap-2 rounded-full bg-saffron px-6 py-3 text-sm font-medium tracking-wide hover:bg-saffron-light transition"
            >
              Explore this place <span>→</span>
            </button>
          </form>
        </div>

        <div className="col-span-12 lg:col-span-7 lg:pl-16">
          {/* Result card */}
          <motion.div
            key={result ? `${result.state}-${result.district}` : "static"}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            data-testid={GEO.result}
            className="border border-ivory/20 rounded-2xl p-8 md:p-10 bg-tulsi-deep/60 backdrop-blur"
          >
            <div className="flex items-center gap-3 text-[10px] tracking-[0.28em] uppercase text-ivory/50">
              <span>Selected</span><span className="w-4 h-px bg-ivory/30" />
              <span>Preview</span>
            </div>
            <h3 className="mt-3 font-serif text-3xl md:text-4xl leading-tight">
              {result ? `${result.district}, ${result.state}` : `${state}`}
              <span className="italic text-marigold text-2xl md:text-3xl ml-2">— {current?.motif || "the land of your kin"}</span>
            </h3>
            <div className="mt-6 grid grid-cols-3 gap-6">
              <div>
                <div className="font-serif text-3xl">{current?.trees ?? "—"}</div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-ivory/60 mt-1">Trees in state</div>
              </div>
              <div>
                <div className="font-serif text-3xl">{current?.districts ?? "—"}</div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-ivory/60 mt-1">Districts</div>
              </div>
              <div>
                <div className="font-serif text-3xl">India</div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-ivory/60 mt-1">Country</div>
              </div>
            </div>

            {/* State chips */}
            <div className="mt-8">
              <div className="text-[10px] uppercase tracking-[0.28em] text-ivory/50">All Indian states</div>
              <div className="mt-4 flex flex-wrap gap-2">
                {states.map((s) => (
                  <button
                    key={s.name}
                    type="button"
                    onClick={() => setState(s.name)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition ${
                      state === s.name
                        ? "bg-marigold text-ink border-marigold"
                        : "border-ivory/25 text-ivory/70 hover:border-ivory hover:text-ivory"
                    }`}
                  >
                    {s.name}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Explore beyond India */}
          <div className="mt-6 flex items-center justify-between gap-4 border border-dashed border-ivory/20 rounded-2xl p-6">
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-ivory/50">The diaspora</div>
              <div className="mt-1 font-serif text-xl">Explore other regions of the network</div>
            </div>
            <a href="#final-cta" className="text-sm text-marigold ink-link">Beyond India →</a>
          </div>
        </div>
      </div>
    </section>
  );
}
